'use client';

import { type ReactNode, useState } from 'react';
import Link from 'next/link';
import { Instagram } from 'lucide-react';
import type { PublicInstagramProductPhoto } from '@/lib/api';
import { instagramPermalink, instagramPreviewUrl } from '@/lib/instagram-media-url';

/** Public consent belongs to the API. Keep uploaded images in the caller's fallback. */
export function InstagramProductPhoto({ photo, title, children, className = '',
  imageClassName = 'h-full w-full object-cover', imageHref, onImageClick, compactSource = false,
}: {
  photo?: PublicInstagramProductPhoto | null;
  title: string;
  children: ReactNode;
  className?: string;
  imageClassName?: string;
  imageHref?: string;
  onImageClick?: () => void;
  compactSource?: boolean;
}) {
  const preview = instagramPreviewUrl(photo?.previewUrl ?? null);
  const source = instagramPermalink(photo?.permalink ?? null);
  const identity = JSON.stringify([photo?.id, preview, source]);
  const [failed, setFailed] = useState<string | null>(null);
  const visible = preview && source && failed !== identity;
  const author = photo?.username ? `@${photo.username.replace(/^@/, '')}` : 'Instagram';
  const image = visible ? (
    // Render expiring provider URLs directly; never retain them in Next's image optimizer.
    // eslint-disable-next-line @next/next/no-img-element
    <img key={identity} src={preview} alt={title} className={imageClassName} loading="lazy" decoding="async"
      referrerPolicy="no-referrer" onError={() => setFailed(identity)} />
  ) : children;
  return (
    <div className={`relative ${className}`}>
      {imageHref ? (
        <Link href={imageHref} prefetch={false} onClick={onImageClick} className="block h-full w-full" aria-label={`View ${title}`}>{image}</Link>
      ) : onImageClick ? (
        <button type="button" onClick={onImageClick} className="block h-full w-full" aria-label={`View ${title}`}>{image}</button>
      ) : image}
      {visible && (
        <a href={source} target="_blank" rel="noopener noreferrer"
          aria-label={`View original Instagram post by ${author} (opens in a new tab)`}
          onClick={event => event.stopPropagation()}
          onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') event.stopPropagation(); }}
          className={`${compactSource ? 'flex w-full justify-center' : 'absolute left-2 top-2 z-10 inline-flex'} min-h-11 items-center gap-1.5 rounded-md bg-black/75 px-2 text-xs font-medium text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}>
          <Instagram className="h-3.5 w-3.5" aria-hidden="true" />{!compactSource && ' Instagram'}
        </a>
      )}
    </div>
  );
}
